import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { Mousewheel, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperRef } from 'swiper/react';
import { classNames } from '../../utils/tailwind/classNames';
import styles from './Carousel.module.scss';
import { useDynamicCarouselConfig } from './useDynamicCarouselConfig';
import { SLIDE_SPACING } from './utils/getCarouselItemsLimit';
import type { Product, Products } from '../../bff-api/products/model';

type CarouselProps = {
    id: string;
    products: Products;
    renderProduct: (product: Product) => ReactNode;
    onViewCarousel?: () => void;
    wrapperClassName?: string;
    swiperClassName?: string;
    swiperSliderClassName?: string;
    slidesOffset?: number;
};

export function useIsVisible(ref: React.MutableRefObject<SwiperRef | null>): boolean {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) =>
            setIntersecting(entry.isIntersecting),
        );

        if (ref?.current?.swiper) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            observer.observe(ref.current.swiper.el);
        }

        return () => {
            observer.disconnect();
        };
    }, [ref]);

    return isIntersecting;
}

export const Carousel = ({
    id,
    products,
    renderProduct,
    onViewCarousel,
    swiperClassName = '',
    wrapperClassName = '',
    swiperSliderClassName = '',
    slidesOffset = 0,
}: CarouselProps): JSX.Element => {
    const shouldShowNavigation = useBreakpointValue({ base: false, 'lg-sm': true });

    const [carouselViewed, setCarouselViewed] = useState(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const ref = useRef<SwiperRef | null>(null);
    const isVisible = useIsVisible(ref);
    const { slidesPerGroup, limitedProducts } = useDynamicCarouselConfig({ ref, products });

    const shouldEnableNavigation = limitedProducts.length > slidesPerGroup;

    useEffect(() => {
        if (onViewCarousel && isVisible && !carouselViewed) {
            setCarouselViewed(true);
            onViewCarousel();
        }
    }, [onViewCarousel, isVisible, carouselViewed]);

    useEffect(() => {
        const handleMouseEnter = (): void => {
            setIsHovered(true);
        };

        const handleMouseLeave = (): void => {
            setIsHovered(false);
        };

        const swiper = document.getElementById(id);

        swiper?.addEventListener('mouseenter', handleMouseEnter);
        swiper?.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            swiper?.removeEventListener('mouseenter', handleMouseEnter);
            swiper?.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [id]);

    return (
        <Box className={`${styles.carouselSwiper} ${wrapperClassName} w-full max-w-full`}>
            <Swiper
                id={id}
                ref={ref}
                slidesPerView="auto"
                spaceBetween={SLIDE_SPACING}
                slidesPerGroup={slidesPerGroup}
                pagination={{
                    clickable: true,
                }}
                rewind
                modules={[Pagination, Scrollbar, Navigation, Mousewheel]}
                lazyPreloadPrevNext={6}
                navigation={shouldShowNavigation && isHovered && shouldEnableNavigation}
                scrollbar
                mousewheel={{ forceToAxis: true }}
                wrapperClass={classNames('gap-4 ', swiperClassName)}
                slidesOffsetAfter={slidesOffset}
                slidesOffsetBefore={slidesOffset}
            >
                {limitedProducts.map((product, index) => (
                    <SwiperSlide
                        virtualIndex={index}
                        key={product.id}
                        className={swiperSliderClassName}
                    >
                        {renderProduct(product)}
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );
};
